import * as React from 'react'

import { Info } from 'lucide-react'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import ExpandableText from '~/client/src/shared/components/ExpandableText/ExpandableText'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'

// localization: translated

interface IProps {
  label: string
  isRequired: boolean
  value: string
  /** Shown behind an info icon beside the label. Omit for no icon. */
  labelTooltip?: string

  isValid?: boolean
  hasShortWidth?: boolean
  isRequiredTextHidden?: boolean
  shouldLineUpLabel?: boolean
}

@observer
export default class StruxhubInputLabel extends React.PureComponent<IProps> {
  public render() {
    const {
      label,
      labelTooltip,
      isRequired,
      hasShortWidth,
      value,
      isValid,
      isRequiredTextHidden,
      shouldLineUpLabel,
    } = this.props
    const { required, optional } = Localization.translator
    const requiredTxt = isRequired ? required : optional
    const requiredMsg = hasShortWidth
      ? `${requiredTxt.substring(0, 3)}.`
      : requiredTxt

    // Mutually exclusive by construction, so this stays a single color class —
    // two Tailwind text-color utilities on one element would tie on specificity.
    const colorClass =
      value && !isValid
        ? 'tw:text-destructive'
        : (isRequired ? !!value && isValid : isValid)
          ? 'tw:text-muted-foreground'
          : isRequired && !value
            ? 'tw:text-primary'
            : 'tw:text-foreground'

    return (
      <div
        className={classList({
          // Centered, so the required text sits in the middle of the label's
          // height however many lines it wraps to.
          'tw:flex tw:items-center tw:justify-between': true,
          'tw:px-2': !shouldLineUpLabel,
        })}
      >
        {/* Grouped so the icon sits against the END OF THE TEXT. Left as a bare child it
            would be pushed to the far right by the label's flex-1, landing next to the
            required text instead of the word it explains. */}
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center">
          {this.renderLabel(label, colorClass, !!labelTooltip)}
          {!!labelTooltip && (
            // Same shape as the controls table's info affordance, down to the width cap.
            <Tooltip>
              <TooltipTrigger
                type="button"
                aria-label={labelTooltip}
                className="tw:ml-1 tw:inline-flex tw:shrink-0 tw:cursor-default tw:items-center tw:text-muted-foreground tw:hover:text-foreground"
              >
                <Info className="tw:size-3.5" />
              </TooltipTrigger>
              <TooltipContent className="tw:max-w-[280px] tw:whitespace-pre-line">
                {labelTooltip}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {!isRequiredTextHidden && (
          <span className="tw:ml-[5px] tw:whitespace-nowrap tw:text-body-sm tw:italic tw:text-muted-foreground">
            {requiredMsg}
          </span>
        )}
      </div>
    )
  }

  private renderLabel(
    label: string,
    colorClass: string,
    hasTrailingIcon: boolean,
  ): JSX.Element {
    return (
      <ExpandableText
        text={label}
        className={classList({
          // `block` keeps scrollHeight meaningful for the clamp; `flex-1`
          // replaces the flex:1 the legacy `.row > *` rule used to supply.
          'tw:block tw:min-w-0 tw:text-left tw:text-label-lg-prominent tw:break-words': true,
          // Dropped when an icon follows, so the text sizes to its content and the icon
          // sits beside it; min-w-0 still lets a long label shrink and clamp.
          'tw:flex-1': !hasTrailingIcon,
          [colorClass]: true,
        })}
      />
    )
  }
}
